import { queryOptions } from '@tanstack/react-query';
import moment from 'moment';
import { type PlacementDetails } from 'src/store/placementDetailsPageSlice/types';

import accommodationService from './accommodationService';

// **** Functions **** //

/**
 * Get placement detail data.
 */
const formatPlacementDetailsPayload = (payload: PlacementDetails) => {
  const newRevisions = payload.revisions
    // Normalize revision date to epoch milliseconds
    .map((revision) => ({
      ...revision,
      revisionDate: new Date(revision.revisionDate).getTime(),
    }))
    // Sort by revisionDate
    .sort((a, b) => new Date(a.revisionDate).getTime() - new Date(b.revisionDate).getTime());

  return {
    ...payload,
    startAtDate: moment(payload.startAtDate).format('L'),
    endAtDate: moment(payload.endAtDate).format('L'),
    transferredAtDate: moment(payload.transferredAtDate).format('L'),
    revisions: newRevisions,
  };
};

// **** Queries **** //

export const getAccommodationQueryOptions = {
  accommodationDetails: (accommodationId: number) =>
    queryOptions({
      queryKey: ['accommodation', accommodationId, 'accommodationDetail'] as const,
      queryFn: async () => (await accommodationService.getAccommodationDetail(accommodationId)).data,
      select: formatPlacementDetailsPayload,
      enabled: !!accommodationId,
    }),
  accommodationIdByCaseIdAndLogin: (type?: string, caseId?: string, login?: string) =>
    queryOptions({
      queryKey: ['accommodation', 'type', type, 'caseId', caseId, 'login', login] as const,
      queryFn: async () => (await accommodationService.getAccommodationIdByCaseIdAndLogin(type!, caseId!, login!)).data,
      enabled: !!type && !!caseId && !!login,
    }),
  emailTemplate: () =>
    queryOptions({
      queryKey: ['emailTemplate', 'saveAccommodation'] as const,
      queryFn: async () => (await accommodationService.getEmailTemplate()).data,
    }),
  activePlacementMetrics: (siteId: number, accommodationType?: string) =>
    queryOptions({
      // placementMetricsBandContainer reads siteId from index 1
      queryKey: ['/accommodation/activePlacementMetrics/site', siteId, { accommodationType }] as const,
      queryFn: async ({ signal }) =>
        (await accommodationService.getActivePlacementMetrics(siteId, accommodationType, { signal })).data,
      // Snapshot like the placements table, which doesn't refresh on focus either
      refetchOnWindowFocus: false,
      // The band shows its own inline error
      meta: { skipErrorToast: true },
    }),
};

// **** Mutations **** //

export const getAccommodationMutationOptions = {
  delete: () => ({
    mutationFn: accommodationService.deleteAccommodation,
    meta: {
      successMessage: 'Deleted accommodation successfully',
    },
  }),
};
